import { useMemo } from 'react'

import { formatInt, NO_VALUE } from '../../lib/format.ts'
import type { ThroughputPoint } from '../../lib/sse/types.ts'
import { ThroughputChart } from '../charts/ThroughputChart.tsx'
import { Metric } from '../primitives/Metric.tsx'
import { Panel } from '../primitives/Panel.tsx'

interface Stats {
  current: number | null
  peak: number | null
  trough: number | null
  mean: number
  minutes: number
}

function summarise(points: readonly ThroughputPoint[]): Stats {
  if (points.length === 0) return { current: null, peak: null, trough: null, mean: 0, minutes: 0 }

  let peak = Number.NEGATIVE_INFINITY
  let trough = Number.POSITIVE_INFINITY
  let sum = 0
  for (const point of points) {
    if (point.events_per_sec > peak) peak = point.events_per_sec
    if (point.events_per_sec < trough) trough = point.events_per_sec
    sum += point.events_per_sec
  }

  const first = points[0]
  const last = points[points.length - 1]
  const spanMs =
    first === undefined || last === undefined ? 0 : Date.parse(last.t) - Date.parse(first.t)

  return {
    current: last?.events_per_sec ?? null,
    peak,
    trough,
    mean: sum / points.length,
    minutes: Math.round(spanMs / 60_000),
  }
}

/**
 * Throughput over the retained window.
 *
 * Wide and short: the question is the shape of the last half hour, not a precise
 * reading at any one minute — those come from the numbers on the left, which are
 * printed rather than left to be estimated off an axis.
 */
export function ThroughputPanel({
  points,
  className,
}: {
  points: readonly ThroughputPoint[]
  className?: string
}) {
  const stats = useMemo(() => summarise(points), [points])

  return (
    <Panel
      title="Throughput recebido"
      className={className}
      meta={
        <span>
          janela de <span className="tnum">{stats.minutes || NO_VALUE}</span> min ·{' '}
          <span className="tnum">{formatInt(points.length)}</span> pontos
        </span>
      }
    >
      <div className="flex h-full gap-4">
        <div className="flex w-32 shrink-0 flex-col justify-between gap-2">
          <Metric
            label="agora"
            value={formatInt(stats.current)}
            unit="ev/s"
            size="lg"
            tone="accent"
          />
          <div className="space-y-1.5">
            <Metric label="pico na janela" value={formatInt(stats.peak)} size="sm" />
            <Metric label="média" value={formatInt(Math.round(stats.mean))} size="sm" />
            <Metric label="mínimo" value={formatInt(stats.trough)} size="sm" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <ThroughputChart points={points} mean={stats.mean} height={148} />
        </div>
      </div>
    </Panel>
  )
}
